/**
 * Templates HTML email B2B prospection — Phase 62
 *
 * Compatibilité clients mail :
 * - Tables HTML imbriquées (Outlook 2007+ safe)
 * - CSS 100 % inline (pas de classes)
 * - Largeur fixe 600 px (mobile responsive via max-width:100%)
 * - Pas de Flexbox/Grid
 * - Pas de web-fonts (fallback `Georgia, serif` + `Helvetica, sans-serif`)
 *
 * Variables (placeholders simples remplacés en JS) :
 *   {{PRENOM}}    → prénom du contact
 *   {{ENSEIGNE}}  → nom de l'enseigne
 *   {{VILLE}}     → ville (optionnel — vide si non renseigné)
 *
 * Couleurs alignées sur la boutique : noir #050505 / or #C9A95A / crème #F2ECE4
 */

const COLORS = {
  black: "#050505",
  gold: "#C9A95A",
  cream: "#F2ECE4",
  green: "#1F4E3D",
  textMuted: "#737373",
};

const BASE_URL = "https://maison-thai.ch";

/** Helper : remplace les placeholders dans une string HTML/texte. */
export function renderTemplate(template, vars) {
  const ville = (vars.ville || "").trim();
  // Phrase facultative selon présence de la ville
  const villeInline = ville ? ` à ${ville}` : "";
  const enseigneFull = ville ? `${vars.enseigne} ${ville}` : vars.enseigne;
  // Phase 101ch (18/02/2026) — Accroche éditable depuis Paramètres (`b2b_outreach_intro`).
  // Fallback : ancienne formulation « cuisine artisanale valaisanne » si le champ est vide.
  const intro = (vars.intro || "").trim() || "cuisine artisanale valaisanne";
  return template
    .replaceAll("{{PRENOM}}", vars.prenom || "")
    .replaceAll("{{ENSEIGNE}}", vars.enseigne || "")
    .replaceAll("{{VILLE}}", ville)
    .replaceAll("{{VILLE_INLINE}}", villeInline)
    .replaceAll("{{ENSEIGNE_FULL}}", enseigneFull)
    .replaceAll("{{INTRO}}", intro);
}

/** Sujets disponibles par variante de template. L'admin choisit lequel copier. */
export const SUBJECTS = {
  first_contact: [
    "Une carte chaude pour {{ENSEIGNE}} ?",
    "Pad thaï prêt-à-vendre — proposition pour {{ENSEIGNE}}",
    "{{PRENOM}}, j'aimerais vous présenter Maison Thaï Conthey",
  ],
  follow_up: [
    "{{PRENOM}}, suite à notre échange — Maison Thaï",
    "Conditions revendeur pour {{ENSEIGNE}} (suite)",
    "Petit récap après notre visite chez {{ENSEIGNE}}",
  ],
};

/** Variante A — premier contact froid. Présente la boutique. */
export const TEMPLATE_FIRST_CONTACT = `
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background:${COLORS.cream};margin:0;padding:24px 0;font-family:Georgia,serif;">
  <tr><td align="center">
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width:600px;background:${COLORS.cream};">

      <!-- HEADER NOIR avec logo image (Sayonara corporate) -->
      <tr><td style="background:${COLORS.black};padding:36px 24px 28px;text-align:center;">
        <img src="${BASE_URL}/logo-maisonthai-name.png" alt="Maison Thaï Conthey" width="320" style="display:block;margin:0 auto;max-width:80%;height:auto;border:0;" />
        <div style="font-family:Helvetica,Arial,sans-serif;font-size:10px;color:${COLORS.gold};letter-spacing:4px;text-transform:uppercase;margin-top:14px;">
          Cuisine artisanale &middot; Conthey VS
        </div>
      </td></tr>

      <!-- HERO TITRE -->
      <tr><td style="background:${COLORS.black};padding:0 24px 40px;text-align:center;">
        <div style="font-family:Georgia,serif;font-size:26px;line-height:1.3;color:${COLORS.cream};font-style:italic;padding:0 12px;">
          « Et si vos clients trouvaient<br/>
          un vrai pad thaï chez vous, {{PRENOM}} ? »
        </div>
      </td></tr>

      <!-- BODY -->
      <tr><td style="background:#FFFFFF;padding:32px 32px 8px;font-family:Helvetica,Arial,sans-serif;font-size:15px;line-height:1.6;color:#1A1C1A;">
        <p style="margin:0 0 16px;">Bonjour {{PRENOM}},</p>
        <p style="margin:0 0 16px;">
          Nous sommes <strong>Maison Thaï Conthey</strong>, {{INTRO}}.
          Nos barquettes scellées MAP — prêtes à réchauffer en 2 minutes — sont déjà
          servies dans plusieurs points de vente du Valais central.
        </p>
        <p style="margin:0 0 16px;">
          Concrètement, voici ce que nous proposons à un commerce comme <strong>{{ENSEIGNE_FULL}}</strong> :
        </p>
        <ul style="margin:0 0 16px;padding-left:20px;">
          <li style="margin-bottom:6px;">DLC <strong>8 à 10 jours</strong>, conservation ≤ 5°C</li>
          <li style="margin-bottom:6px;">Recettes artisanales préparées maison à Conthey</li>
          <li style="margin-bottom:6px;">Sans royalties, sans droits d&apos;entrée</li>
          <li style="margin-bottom:6px;"><strong>Livraison directe</strong> par nos soins dans la région</li>
        </ul>
      </td></tr>

      <!-- CTA PRINCIPAL -->
      <tr><td style="background:#FFFFFF;padding:16px 32px 32px;text-align:center;">
        <a href="${BASE_URL}/boutique"
           style="display:inline-block;background:${COLORS.gold};color:${COLORS.black};font-family:Helvetica,Arial,sans-serif;font-size:13px;font-weight:600;letter-spacing:3px;text-decoration:none;padding:16px 32px;border-radius:2px;text-transform:uppercase;">
          Découvrir la boutique &nbsp;&rarr;
        </a>
        <div style="font-family:Helvetica,Arial,sans-serif;font-size:11px;color:${COLORS.textMuted};margin-top:14px;letter-spacing:1px;">
          Barquettes individuelles dès 18.50 CHF
        </div>
      </td></tr>

      <!-- TRUST SIGNALS -->
      <tr><td style="background:#FFFFFF;padding:0 32px 32px;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-top:1px solid #E5E5E0;border-bottom:1px solid #E5E5E0;">
          <tr>
            <td align="center" style="padding:14px 4px;font-family:Helvetica,Arial,sans-serif;font-size:11px;color:${COLORS.textMuted};letter-spacing:2px;text-transform:uppercase;">
              ★ Production Conthey
            </td>
            <td align="center" style="padding:14px 4px;font-family:Helvetica,Arial,sans-serif;font-size:11px;color:${COLORS.textMuted};letter-spacing:2px;text-transform:uppercase;">
              ★ HACCP certifié
            </td>
            <td align="center" style="padding:14px 4px;font-family:Helvetica,Arial,sans-serif;font-size:11px;color:${COLORS.textMuted};letter-spacing:2px;text-transform:uppercase;">
              ★ Traçabilité lot
            </td>
          </tr>
        </table>
      </td></tr>

      <!-- SECTION PARTENAIRE -->
      <tr><td style="background:${COLORS.cream};padding:36px 32px;text-align:center;">
        <div style="font-family:Helvetica,Arial,sans-serif;font-size:10px;color:${COLORS.green};letter-spacing:3px;text-transform:uppercase;margin-bottom:12px;">
          Pour les commerces
        </div>
        <div style="font-family:Georgia,serif;font-size:20px;color:${COLORS.black};line-height:1.4;margin-bottom:8px;">
          Vous voulez voir nos tarifs revendeur ?
        </div>
        <div style="font-family:Helvetica,Arial,sans-serif;font-size:13px;color:#575A57;line-height:1.6;margin-bottom:20px;">
          Conditions négociées selon le volume mensuel et la saisonnalité.<br/>
          Présentation sur rendez-vous, sans engagement.
        </div>
        <a href="${BASE_URL}/devenir-revendeur"
           style="display:inline-block;font-family:Helvetica,Arial,sans-serif;font-size:12px;color:${COLORS.green};letter-spacing:3px;text-decoration:none;text-transform:uppercase;border:1px solid ${COLORS.green};padding:12px 24px;border-radius:2px;">
          Devenir partenaire &nbsp;&rarr;
        </a>
      </td></tr>

      <!-- SIGNATURE -->
      <tr><td style="background:#FFFFFF;padding:32px 32px 24px;font-family:Helvetica,Arial,sans-serif;font-size:14px;color:#1A1C1A;line-height:1.6;">
        <p style="margin:0 0 4px;">Avec plaisir d&apos;échanger,</p>
        <p style="margin:0;font-weight:600;">L&apos;équipe Maison Thaï Conthey</p>
        <p style="margin:8px 0 0;font-size:12px;color:${COLORS.textMuted};">
          <a href="mailto:info@maison-thai.ch" style="color:${COLORS.green};text-decoration:none;">info@maison-thai.ch</a>
          &nbsp;·&nbsp;
          <a href="tel:+41273460775" style="color:${COLORS.green};text-decoration:none;">+41 27 346 07 75</a>
        </p>
      </td></tr>

      <!-- FOOTER -->
      <tr><td style="background:${COLORS.black};padding:20px 32px;text-align:center;font-family:Helvetica,Arial,sans-serif;font-size:10px;color:#888;letter-spacing:1px;line-height:1.5;">
        Maison Thaï &middot; Rue de Parcouret 22 &middot; CH-1964 Conthey<br/>
        <a href="${BASE_URL}" style="color:${COLORS.gold};text-decoration:none;">maison-thai.ch</a>
      </td></tr>

    </table>
  </td></tr>
</table>
`.trim();

/** Variante B — suivi après visite / contact précédent. Plus chaud, plus court. */
export const TEMPLATE_FOLLOW_UP = `
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background:${COLORS.cream};margin:0;padding:24px 0;font-family:Georgia,serif;">
  <tr><td align="center">
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width:600px;background:${COLORS.cream};">

      <!-- HEADER NOIR avec logo image (Sayonara corporate) -->
      <tr><td style="background:${COLORS.black};padding:32px 24px 22px;text-align:center;">
        <img src="${BASE_URL}/logo-maisonthai-name.png" alt="Maison Thaï Conthey" width="280" style="display:block;margin:0 auto;max-width:75%;height:auto;border:0;" />
      </td></tr>

      <!-- BODY -->
      <tr><td style="background:#FFFFFF;padding:36px 32px 8px;font-family:Helvetica,Arial,sans-serif;font-size:15px;line-height:1.6;color:#1A1C1A;">
        <p style="margin:0 0 16px;">Bonjour {{PRENOM}},</p>
        <p style="margin:0 0 16px;">
          Suite à notre échange, je reviens vers vous avec quelques liens utiles
          pour <strong>{{ENSEIGNE_FULL}}</strong> :
        </p>
        <p style="margin:0 0 8px;">
          &rarr; <strong>Notre catalogue complet</strong> (photos, allergènes,
          DLC, valeurs nutritionnelles) :
        </p>
        <p style="margin:0 0 16px;">
          <a href="${BASE_URL}/boutique" style="color:${COLORS.green};font-weight:600;text-decoration:underline;">maison-thai.ch/boutique</a>
        </p>
        <p style="margin:0 0 8px;">
          &rarr; <strong>Conditions revendeur</strong> (tarifs négociés, processus de mise en rayon) :
        </p>
        <p style="margin:0 0 24px;">
          <a href="${BASE_URL}/devenir-revendeur" style="color:${COLORS.green};font-weight:600;text-decoration:underline;">maison-thai.ch/devenir-revendeur</a>
        </p>
        <p style="margin:0 0 16px;">
          Je reste à votre disposition pour discuter d&apos;un essai sur quelques semaines,
          ou pour passer vous voir directement à <strong>{{VILLE}}</strong>.
        </p>
      </td></tr>

      <!-- CTA -->
      <tr><td style="background:#FFFFFF;padding:8px 32px 36px;text-align:center;">
        <a href="${BASE_URL}/boutique"
           style="display:inline-block;background:${COLORS.gold};color:${COLORS.black};font-family:Helvetica,Arial,sans-serif;font-size:13px;font-weight:600;letter-spacing:3px;text-decoration:none;padding:14px 28px;border-radius:2px;text-transform:uppercase;margin:0 4px 8px;">
          Voir la boutique
        </a>
        <a href="${BASE_URL}/devenir-revendeur"
           style="display:inline-block;background:transparent;color:${COLORS.green};font-family:Helvetica,Arial,sans-serif;font-size:13px;font-weight:600;letter-spacing:3px;text-decoration:none;padding:13px 28px;border:1px solid ${COLORS.green};border-radius:2px;text-transform:uppercase;margin:0 4px 8px;">
          Devenir partenaire
        </a>
      </td></tr>

      <!-- SIGNATURE -->
      <tr><td style="background:#FFFFFF;padding:0 32px 32px;font-family:Helvetica,Arial,sans-serif;font-size:14px;color:#1A1C1A;line-height:1.6;">
        <p style="margin:0 0 4px;">Bien à vous,</p>
        <p style="margin:0;font-weight:600;">L&apos;équipe Maison Thaï Conthey</p>
        <p style="margin:8px 0 0;font-size:12px;color:${COLORS.textMuted};">
          <a href="mailto:info@maison-thai.ch" style="color:${COLORS.green};text-decoration:none;">info@maison-thai.ch</a>
          &nbsp;·&nbsp;
          <a href="tel:+41273460775" style="color:${COLORS.green};text-decoration:none;">+41 27 346 07 75</a>
        </p>
      </td></tr>

      <!-- FOOTER -->
      <tr><td style="background:${COLORS.black};padding:20px 32px;text-align:center;font-family:Helvetica,Arial,sans-serif;font-size:10px;color:#888;letter-spacing:1px;line-height:1.5;">
        Maison Thaï &middot; Rue de Parcouret 22 &middot; CH-1964 Conthey<br/>
        <a href="${BASE_URL}" style="color:${COLORS.gold};text-decoration:none;">maison-thai.ch</a>
      </td></tr>

    </table>
  </td></tr>
</table>
`.trim();

export const TEMPLATES = {
  first_contact: {
    label: "Premier contact (cold)",
    description: "Email d'introduction. Présente la boutique + CTA partenaire en bas.",
    html: TEMPLATE_FIRST_CONTACT,
  },
  follow_up: {
    label: "Suivi (warm)",
    description: "Email plus court. À envoyer après une visite ou un appel.",
    html: TEMPLATE_FOLLOW_UP,
  },
};
